import { CRORE, toTitleCase, validateCIN, validateLLPIN } from '../../../../../../utils/utils';
import { DATE_FORMATS, formatDateByFormat } from '../../../../../../utils/dateUtils';
import { ENTITY_VISUAL_TYPE } from '../../../../../../utils/constants/commonConstants';
import { formatShareholdingPercent } from '../../../../../../utils/formatShareholdingPercent';
import { toNumberOrNull } from '../../../../../../store/dashboard/compliancesBefe/mappers';
import { formatFyFromEndYear, formatRptAmount } from '../../../../../../store/dashboard/relatedParties/relatedPartyTransactions_mappers';
import type { AicaStatus, EntityDetails, RptParty } from '../../../../../../store/dashboard/relatedParties/relatedPartyTransactions.types';
import {
    ENTITY_HOVER_LABELS as L,
    ENTITY_STATUS_ACTIVE,
    ENTITY_STATUS_PILL,
    FINANCIALS_LABEL,
    FINANCIALS_NOT_AVAILABLE,
    ROW_DETAILS_LOCKED,
    ROW_DETAILS_SEPARATOR,
    SECOND_DEGREE,
} from './EntityHoverCard.constants';
import type {
    EntityHoverProfile,
    HoverFinancialValue,
    HoverStatRow,
    NoDataCardExtras,
    RowFinancialsDetails,
} from './EntityHoverCard.types';
import { RPT_BUCKET, RPT_EMPTY_VALUE as EMPTY } from '../../RelatedPartyTransactions/relatedPartyTransactions_constants';

export interface EntityHoverRow {
    fromKid?: string;
    toKid?: string;
    fromName?: string;
    degree?: string;
    edgeType?: string;
    type?: string;
    edgeValue?: string;
    shareholdingPercentage?: number | null;
    entityDetails?: EntityDetails;
    aicaStatus?: AicaStatus;
    /** Replaces the humanised edgeType on the Relationship row, e.g. an Other KMP party's MCA relationship. */
    relationshipText?: string;
    /** Drops "—" rows: set only for Other KMP parties, so existing related-party cards keep every row. */
    presentRowsOnly?: boolean;
}

const LLP_TYPES = new Set(['LLP', 'LIMITED_LIABILITY_PARTNERSHIP']);

const hasText = (value: string | null | undefined): value is string => !!value?.trim();

/** INR short form with the rupee sign after any minus: 4500000000 → "₹450.00 Cr", null → "—". */
const formatHoverMoney = (value: unknown): string => {
    const amount = toNumberOrNull(value);
    if (amount === null) return EMPTY;
    return amount < 0 ? `-₹${formatRptAmount(-amount)}` : `₹${formatRptAmount(amount)}`;
};

const formatPlainAmount = (value: unknown): string => {
    const amount = toNumberOrNull(value);
    return amount === null ? EMPTY : formatRptAmount(amount);
};

const formatHoverDate = (value: string | null | undefined): string =>
    formatDateByFormat(value ?? undefined, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY) || EMPTY;

const humaniseEdgeType = (edgeType?: string): string => (edgeType ? toTitleCase(edgeType.replace(/_/g, ' ')) : EMPTY);

const toWebsite = (url: string | null | undefined): { href: string; text: string } | null => {
    if (!hasText(url)) return null;
    const trimmed = url.trim();
    const href = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
    if (!href.toLowerCase().startsWith('https:')) return null;
    return { href, text: href.replace(/^https:\/\//i, '').replace(/\/$/, '') };
};

const financialValues = (financials: AicaStatus['financials'], netWorthLabel: string): HoverFinancialValue[] => [
    { label: L.REVENUE, value: formatHoverMoney(financials?.revenue) },
    { label: L.PAT, value: formatHoverMoney(financials?.pat) },
    { label: netWorthLabel, value: formatHoverMoney(financials?.netWorth) },
];

const canShowCta = (aica: AicaStatus, kid: string): boolean => (aica.state === 'ADD' ? !!kid : !!aica.aicaOrgId);

const formatInvestors = (investors: string[] | null | undefined): string | null => {
    const names = (investors ?? []).filter(hasText);
    if (!names.length) return null;
    return names.length === 1 ? names[0] : `${names[0]} (+${names.length - 1} more)`;
};

/** The row's own CIN/LLPIN: degree-2 director-network rows carry it on toKid. */
const entityKidOf = (row: EntityHoverRow): string => (row.degree === SECOND_DEGREE ? row.toKid : row.fromKid) ?? '';

/** Hover-card shareholding without trailing zeros: 30.00 → "30%", 30.50 → "30.5%". */
const formatHoverShareholding = (value: number | null | undefined): string =>
    formatShareholdingPercent(value, { nullText: EMPTY }).replace(/(\.\d*[1-9])0+%$|\.0+%$/, '$1%');

/** polus leaves shareholdingPercentage null on JV and LLP rows, whose edgeValue still carries the % ("50", "60.00%"). */
const edgeValuePercent = (edgeValue: string | undefined): number | null => toNumberOrNull(edgeValue?.trim().replace(/%$/, ''));

/** FY label of a VIEW org's audited financials; null when not VIEW or no audited year (financials null or all-null). */
const viewFinancialYearLabel = (aica: AicaStatus | undefined): string | null =>
    aica?.state === 'VIEW' ? formatFyFromEndYear(aica.financials?.financialYear) : null;

/** Same text as the Funding History total row (SummaryRow): 455000000 → "45.50 Cr". */
const formatFundingTotal = (amount: number): string => `${(amount / CRORE).toFixed(2)} Cr`;

export function toEntityHoverProfile(row: EntityHoverRow): EntityHoverProfile {
    const details = row.entityDetails ?? {};
    const aica = row.aicaStatus;
    const kid = entityKidOf(row);
    const isLlp = details.entityCategory?.toLowerCase() === 'llp' || LLP_TYPES.has(String(row.type ?? '').toUpperCase());
    const rawName = hasText(details.legalName) ? details.legalName : row.fromName ?? '';
    const isView = aica?.state === 'VIEW';
    const website = isView ? toWebsite(aica?.websiteUrl) : null;
    const status = details.status?.trim();
    const isActive = status?.toUpperCase() === ENTITY_STATUS_ACTIVE;

    const detailRows: HoverStatRow[] = [
        { label: L.INCORPORATION_DATE, value: formatHoverDate(details.incorporationDate) },
        status
            ? {
                  label: L.ENTITY_STATUS,
                  value: isActive ? ENTITY_STATUS_PILL.ACTIVE : ENTITY_STATUS_PILL.INACTIVE,
                  asPill: true,
                  tone: isActive ? 'positive' : 'negative',
              }
            : { label: L.ENTITY_STATUS, value: EMPTY },
    ];
    const fundingRaised = isView ? toNumberOrNull(aica?.financials?.fundingRaised) : null;
    if (fundingRaised !== null) detailRows.push({ label: L.FUNDING_RAISED, value: formatFundingTotal(fundingRaised), compact: true });
    const investors = isView ? formatInvestors(aica?.financials?.investors) : null;
    if (investors) detailRows.push({ label: L.INVESTORS, value: investors, compact: true });
    if (hasText(details.activeCompliance)) {
        const compliance = details.activeCompliance.trim();
        detailRows.push({
            label: L.ACTIVE_COMPLIANCE,
            value: compliance,
            ...(/^active/i.test(compliance) && { tone: 'positive' as const }),
        });
    }
    detailRows.push(
        isLlp
            ? { label: L.TOTAL_OBLIGATION, value: formatPlainAmount(details.totalObligationOfContribution) }
            : { label: L.PAID_UP_CAPITAL, value: formatPlainAmount(details.paidUpCapital) },
        { label: L.SUM_OF_CHARGES, value: formatPlainAmount(details.sumOfCharges) },
        { label: isLlp ? L.LLPIN : L.CIN, value: kid || EMPTY, ...(status && !isActive && { tag: status }), identifier: true },
        { label: L.NEXT_CIN, value: hasText(details.nextCin) ? details.nextCin : EMPTY },
    );
    if (hasText(details.financialYear)) detailRows.push({ label: L.FINANCIAL_YEAR, value: details.financialYear });
    if (hasText(details.cirpStatus)) detailRows.push({ label: L.CIRP_STATUS, value: details.cirpStatus });

    const appointment = (row.degree === SECOND_DEGREE ? details.appointment : null) ?? {};
    if (hasText(appointment.designation)) detailRows.push({ label: L.DESIGNATION, value: appointment.designation });
    if (hasText(appointment.dateOfAppointment))
        detailRows.push({ label: L.DATE_OF_APPOINTMENT, value: formatHoverDate(appointment.dateOfAppointment) });
    if (hasText(appointment.dateOfAppointmentCurrentDesignation))
        detailRows.push({ label: L.CURRENT_DESIGNATION_DATE, value: formatHoverDate(appointment.dateOfAppointmentCurrentDesignation) });
    if (hasText(appointment.dateOfCessation))
        detailRows.push({ label: L.APPOINTMENT_STATUS, value: `Former (ceased ${formatHoverDate(appointment.dateOfCessation)})` });

    const fyLabel = viewFinancialYearLabel(aica);
    const financials = !aica
        ? null
        : fyLabel
        ? { label: `${FINANCIALS_LABEL} (${fyLabel})`, values: financialValues(aica.financials, L.NET_WORTH) }
        : { label: FINANCIALS_NOT_AVAILABLE, values: null };

    const ctaAllowed = !!aica && canShowCta(aica, kid);
    const shown = (rows: HoverStatRow[]) => (row.presentRowsOnly ? rows.filter((statRow) => statRow.value !== EMPTY) : rows);

    return {
        name: toTitleCase(rawName) || EMPTY,
        city: hasText(details.city) ? toTitleCase(details.city) : null,
        websiteHref: website?.href ?? null,
        websiteText: website?.text ?? null,
        financials,
        relationRows: shown([
            { label: L.RELATIONSHIP, value: row.relationshipText ?? humaniseEdgeType(row.edgeType) },
            { label: L.SHAREHOLDING, value: formatHoverShareholding(row.shareholdingPercentage ?? edgeValuePercent(row.edgeValue)) },
        ]),
        detailRows: shown(detailRows),
        cta: ctaAllowed ? { state: aica.state, aicaOrgId: aica.aicaOrgId, prefillName: rawName, prefillIdentifier: kid } : null,
    };
}

/** Details-column content for a non-person row: VIEW with a financial year → the card's Revenue / PAT / Net-Worth; UNLOCK → "Locked"; else null. */
export function toRowFinancialsDetails(aicaStatus: AicaStatus | undefined, entityType: string | undefined): RowFinancialsDetails | null {
    if (isPersonType(entityType)) return null;
    if (aicaStatus?.state === 'UNLOCK') return { kind: 'locked', text: ROW_DETAILS_LOCKED };
    if (!aicaStatus || !viewFinancialYearLabel(aicaStatus)) return null;
    const values = financialValues(aicaStatus.financials, L.ROW_NET_WORTH);
    return { kind: 'financials', values, text: values.map(({ label, value }) => `${label} ${value}`).join(ROW_DETAILS_SEPARATOR) };
}

/** A named non-person row that polus sent no hover data for; it gets the no-data company card. */
export const isNoDataCompanyRow = (row: EntityHoverRow): boolean =>
    !row.entityDetails && !row.aicaStatus && !isPersonType(row.type) && hasText(row.fromName);

/** No-data company card: blurred financials, only the stat rows we have, and an Add CTA prefilled with the name unless the entity has a CIN/LLPIN. */
export const toNoDataEntityHoverProfile = (name: string, prefillName: string, extras: NoDataCardExtras = {}): EntityHoverProfile => ({
    name,
    city: null,
    websiteHref: null,
    websiteText: null,
    financials: { label: FINANCIALS_NOT_AVAILABLE, values: null },
    relationRows: extras.relationRows ?? [],
    detailRows: extras.detailRows ?? [],
    // polus omits aicaStatus only when its status lookup failed, so an entity with a CIN/LLPIN may already be on AICA.
    cta: extras.detailRows?.some((row) => row.identifier) ? null : { state: 'ADD', aicaOrgId: null, prefillName, prefillIdentifier: '' },
});

/** Whether a related-party row type is a person (case-insensitive). */
export function isPersonType(type: unknown): boolean {
    return String(type ?? '').toUpperCase() === ENTITY_VISUAL_TYPE.PERSON;
}

const presentRow = (label: string, value: string | null | undefined): HoverStatRow[] => (hasText(value) ? [{ label, value }] : []);

const isEntityId = (kid: string): boolean => validateCIN(kid) || validateLLPIN(kid);

/** The CIN/LLPIN identifier row; callers pass only a kid that passed isEntityId, or ''. */
const entityIdRows = (kid: string): HoverStatRow[] =>
    kid ? [{ label: validateLLPIN(kid) ? L.LLPIN : L.CIN, value: kid, identifier: true }] : [];

/** No-data card rows of a related-party row: degree 1 gets Relationship, Shareholding and its CIN/LLPIN; degree 2 gets its column-2 relation text + Designation. */
export function toNoDataRowExtras(row: EntityHoverRow & { designation?: string }, relationText?: string): NoDataCardExtras {
    if (row.degree === SECOND_DEGREE) {
        return { relationRows: presentRow(L.RELATIONSHIP, relationText), detailRows: presentRow(L.DESIGNATION, row.designation) };
    }
    const shareholding = row.shareholdingPercentage ?? edgeValuePercent(row.edgeValue);
    // Degree 1 only: a degree-2 toKid can be the parent company's CIN, which would prefill the wrong company.
    const kid = isEntityId(row.fromKid ?? '') ? (row.fromKid as string) : '';
    return {
        relationRows: [
            ...presentRow(L.RELATIONSHIP, row.edgeType && humaniseEdgeType(row.edgeType)),
            ...presentRow(L.SHAREHOLDING, shareholding === null ? null : formatHoverShareholding(shareholding)),
        ],
        detailRows: entityIdRows(kid),
    };
}

/** An Other KMP company/LLP party's CIN/LLPIN; '' for any other kid (a PAN, DIN or nameless key is never shown). */
export function rptEntityKid(party: RptParty): string {
    const kid = party.partyKid ?? '';
    const isEntityBucket = party.bucket === RPT_BUCKET.COMPANY || party.bucket === RPT_BUCKET.LLP;
    return isEntityBucket && isEntityId(kid) ? kid : '';
}

/** No-data card rows of an Other KMP party: Relationship and CIN/LLPIN. */
export function toRptNoDataExtras(party: RptParty): NoDataCardExtras {
    const kid = rptEntityKid(party);
    return {
        relationRows: presentRow(L.RELATIONSHIP, party.relationship),
        detailRows: entityIdRows(kid),
    };
}

/** An Other KMP party polus stamped with AICA status / entity details, shaped as the degree-1 row the full card reads. */
export const toRptHoverRow = (party: RptParty): EntityHoverRow => ({
    fromName: party.partyName,
    fromKid: rptEntityKid(party),
    degree: '1',
    type: party.bucket === RPT_BUCKET.LLP ? ENTITY_VISUAL_TYPE.LLP : ENTITY_VISUAL_TYPE.COMPANY,
    aicaStatus: party.aicaStatus,
    entityDetails: party.entityDetails,
    relationshipText: party.relationship || undefined,
    presentRowsOnly: true,
});
